// src/pages/Storage.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import { ModalConfirm } from '../components/Modal';
import {
    CircleStackIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    PhotoIcon,
    ArrowPathIcon,
    TrashIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    DocumentIcon,
    FolderIcon,
    MagnifyingGlassIcon,
    WrenchScrewdriverIcon,
    ClockIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

const Storage = () => {
    const { executeQuery, executeMutation, queries, mutations, loading } = useGraphQL();

    // State
    const [activeTab, setActiveTab] = useState('stats');
    const [stats, setStats] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [error, setError] = useState(null);
    const [actionResult, setActionResult] = useState(null);

    // Integrity state
    const [integrityReport, setIntegrityReport] = useState(null);
    const [integrityTenderId, setIntegrityTenderId] = useState('');
    const [integrityLimit, setIntegrityLimit] = useState(100);
    const [verifyTenderId, setVerifyTenderId] = useState('');
    const [verifyRepair, setVerifyRepair] = useState(false);
    const [verificationResult, setVerificationResult] = useState(null);
    const [cleanupDryRun, setCleanupDryRun] = useState(true);
    const [cleanupResult, setCleanupResult] = useState(null);

    // Cache state
    const [cacheDocumentId, setCacheDocumentId] = useState('');
    const [cacheAssessmentId, setCacheAssessmentId] = useState('');
    const [cacheStatus, setCacheStatus] = useState(null);
    const [clearAssessmentId, setClearAssessmentId] = useState('');

    // Document Management state
    const [docTenderId, setDocTenderId] = useState('');
    const [tenderDocuments, setTenderDocuments] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [deleteAllDryRun, setDeleteAllDryRun] = useState(true);
    const [deleteAllResult, setDeleteAllResult] = useState(null);

    // Document FTS search state

    // Modals
    const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
    const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);
    const [showClearAssessmentCacheConfirm, setShowClearAssessmentCacheConfirm] = useState(false);
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
    const [showVerifyConfirm, setShowVerifyConfirm] = useState(false);
    const [showDeleteDocumentConfirm, setShowDeleteDocumentConfirm] = useState(false);
    const [showDeleteAllDocumentsConfirm, setShowDeleteAllDocumentsConfirm] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setError(null);
        try {
            const data = await executeQuery(queries.getDocumentStorageStats());
            setStats(data.adminDocumentStorageStats);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load storage stats:', err);
            setError(err.message);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    // Integrity Check
    const handleIntegrityCheck = async () => {
        setError(null);
        setIntegrityReport(null);
        try {
            const variables = { limit: integrityLimit };
            if (integrityTenderId.trim()) {
                variables.tenderId = integrityTenderId.trim();
            }
            const data = await executeQuery(queries.getDocumentIntegrityCheck(), variables);
            setIntegrityReport(data.adminDocumentIntegrityCheck);
        } catch (err) {
            console.error('Failed to run integrity check:', err);
            setError(err.message);
        }
    };

    // Cleanup Orphaned Files
    const handleCleanupOrphaned = async () => {
        setError(null);
        setCleanupResult(null);
        try {
            const result = await executeMutation(mutations.adminCleanupOrphanedFiles, { dryRun: cleanupDryRun });
            setCleanupResult(result.adminCleanupOrphanedFiles);
            if (!cleanupDryRun && result.adminCleanupOrphanedFiles?.success) {
                loadStats(); // Refresh stats after actual cleanup
            }
        } catch (err) {
            console.error('Failed to cleanup orphaned files:', err);
            setError(err.message);
        }
        setShowCleanupConfirm(false);
    };

    // Verify Document Storage
    const handleVerifyStorage = async () => {
        if (!verifyTenderId.trim()) {
            setError('Tender ID is required');
            return;
        }
        setError(null);
        setVerificationResult(null);
        try {
            const result = await executeMutation(mutations.adminVerifyDocumentStorage, {
                tenderId: verifyTenderId.trim(),
                repair: verifyRepair
            });
            setVerificationResult(result.adminVerifyDocumentStorage);
        } catch (err) {
            console.error('Failed to verify document storage:', err);
            setError(err.message);
        }
        setShowVerifyConfirm(false);
    };

    // Check Cache Status
    const handleCheckCacheStatus = async () => {
        if (!cacheDocumentId.trim() || !cacheAssessmentId.trim()) {
            setError('Both Document ID and Assessment ID are required');
            return;
        }
        setError(null);
        setCacheStatus(null);
        try {
            const data = await executeQuery(queries.getEvidenceCacheStatus(), {
                documentId: cacheDocumentId.trim(),
                assessmentId: cacheAssessmentId.trim()
            });
            setCacheStatus(data.adminEvidenceCacheStatus);
        } catch (err) {
            console.error('Failed to check cache status:', err);
            setError(err.message);
        }
    };

    // Clear Evidence Cache
    const handleClearCache = async () => {
        setError(null);
        try {
            const result = await executeMutation(mutations.adminClearEvidenceCache, {
                documentId: cacheDocumentId.trim(),
                assessmentId: cacheAssessmentId.trim()
            });
            if (result.adminClearEvidenceCache?.success) {
                setActionResult({
                    type: 'success',
                    message: `Cache cleared: ${result.adminClearEvidenceCache.filesDeleted} files, ${formatBytes(result.adminClearEvidenceCache.bytesFreed)} freed`
                });
                setCacheStatus(null);
                loadStats();
            } else {
                setActionResult({
                    type: 'error',
                    message: result.adminClearEvidenceCache?.error || 'Failed to clear cache'
                });
            }
        } catch (err) {
            console.error('Failed to clear cache:', err);
            setError(err.message);
        }
        setShowClearCacheConfirm(false);
    };

    // Clear All Assessment Caches
    const handleClearAssessmentCache = async () => {
        if (!clearAssessmentId.trim()) {
            setError('Assessment ID is required');
            return;
        }
        setError(null);
        try {
            const result = await executeMutation(mutations.adminClearAssessmentEvidenceCache, {
                assessmentId: clearAssessmentId.trim()
            });
            if (result.adminClearAssessmentEvidenceCache?.success) {
                setActionResult({
                    type: 'success',
                    message: `Assessment cache cleared: ${result.adminClearAssessmentEvidenceCache.filesDeleted} files, ${formatBytes(result.adminClearAssessmentEvidenceCache.bytesFreed)} freed`
                });
                loadStats();
            } else {
                setActionResult({
                    type: 'error',
                    message: result.adminClearAssessmentEvidenceCache?.error || 'Failed to clear assessment cache'
                });
            }
        } catch (err) {
            console.error('Failed to clear assessment cache:', err);
            setError(err.message);
        }
        setShowClearAssessmentCacheConfirm(false);
    };

    // Regenerate Evidence Images
    const handleRegenerateImages = async () => {
        setError(null);
        try {
            const result = await executeMutation(mutations.adminRegenerateEvidenceImages, {
                documentId: cacheDocumentId.trim(),
                assessmentId: cacheAssessmentId.trim()
            });
            if (result.adminRegenerateEvidenceImages?.success) {
                setActionResult({
                    type: 'success',
                    message: `Images regenerated: ${result.adminRegenerateEvidenceImages.snippetsGenerated} snippets, ${result.adminRegenerateEvidenceImages.fullPagesGenerated} full pages (${result.adminRegenerateEvidenceImages.processingTimeMs}ms)`
                });
                setCacheStatus(null);
                loadStats();
            } else {
                setActionResult({
                    type: 'error',
                    message: result.adminRegenerateEvidenceImages?.error || 'Failed to regenerate images'
                });
            }
        } catch (err) {
            console.error('Failed to regenerate images:', err);
            setError(err.message);
        }
        setShowRegenerateConfirm(false);
    };

    // List Tender Documents
    const handleListTenderDocuments = async (tenderIdOverride) => {
        const tenderId = tenderIdOverride || docTenderId;
        if (!tenderId?.trim()) {
            setError('Tender ID is required');
            return;
        }
        setError(null);
        setTenderDocuments(null);
        setDeleteAllResult(null);
        // Update the input field if an override was provided
        if (tenderIdOverride) {
            setDocTenderId(tenderIdOverride);
        }
        try {
            const result = await executeMutation(mutations.adminListTenderDocuments, {
                tenderId: tenderId.trim()
            });
            if (result.adminListTenderDocuments?.success) {
                setTenderDocuments(result.adminListTenderDocuments);
            } else {
                setError(result.adminListTenderDocuments?.error || 'Failed to list documents');
            }
        } catch (err) {
            console.error('Failed to list tender documents:', err);
            setError(err.message);
        }
    };

    // Delete Single Document
    const handleDeleteDocument = async () => {
        if (!selectedDocument) return;
        setError(null);
        try {
            const result = await executeMutation(mutations.adminDeleteDocument, {
                documentId: selectedDocument.id
            });
            if (result.adminDeleteDocument?.success) {
                setActionResult({
                    type: 'success',
                    message: `Deleted "${result.adminDeleteDocument.documentTitle}": ${result.adminDeleteDocument.filesDeleted} files, ${result.adminDeleteDocument.snippetsCleaned} snippets, ${formatBytes(result.adminDeleteDocument.bytesFreed)} freed`
                });
                // Refresh the document list
                handleListTenderDocuments();
                loadStats();
            } else {
                setActionResult({
                    type: 'error',
                    message: result.adminDeleteDocument?.error || 'Failed to delete document'
                });
            }
        } catch (err) {
            console.error('Failed to delete document:', err);
            setError(err.message);
        }
        setShowDeleteDocumentConfirm(false);
        setSelectedDocument(null);
    };

    // Delete All Tender Documents
    const handleDeleteAllTenderDocuments = async () => {
        if (!docTenderId.trim()) return;
        setError(null);
        setDeleteAllResult(null);
        try {
            const result = await executeMutation(mutations.adminDeleteAllTenderDocuments, {
                tenderId: docTenderId.trim(),
                dryRun: deleteAllDryRun
            });
            if (result.adminDeleteAllTenderDocuments?.success) {
                setDeleteAllResult(result.adminDeleteAllTenderDocuments);
                if (!deleteAllDryRun) {
                    // Refresh the document list after actual deletion
                    handleListTenderDocuments();
                    loadStats();
                }
            } else {
                setDeleteAllResult({
                    success: false,
                    errors: [result.adminDeleteAllTenderDocuments?.error || 'Failed to delete documents']
                });
            }
        } catch (err) {
            console.error('Failed to delete all tender documents:', err);
            setError(err.message);
        }
        setShowDeleteAllDocumentsConfirm(false);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OK': return 'text-green-600';
            case 'REPAIRED': return 'text-blue-600';
            case 'MISSING': return 'text-red-600';
            case 'FAILED': return 'text-red-600';
            default: return 'text-slate-500';
        }
    };

    const getDocumentStateColor = (state) => {
        switch (state) {
            case 'READY': return 'text-green-700 bg-green-100';
            case 'PENDING': return 'text-yellow-700 bg-yellow-100';
            case 'PROCESSING': return 'text-blue-700 bg-blue-100';
            case 'FAILED': return 'text-red-700 bg-red-100';
            case 'CONVERTED': return 'text-green-700 bg-green-100';
            default: return 'text-slate-600 bg-slate-100';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <CircleStackIcon className="h-8 w-8 text-primary" />
                    <h1 className="text-2xl font-bold text-slate-900">Storage Maintenance</h1>
                </div>
                <button
                    onClick={loadStats}
                    className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                    disabled={loading}
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Action Result Display */}
            {actionResult && (
                <div className={`${actionResult.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
                    <div className="flex items-center space-x-2">
                        {actionResult.type === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        ) : (
                            <XCircleIcon className="h-5 w-5 text-red-600" />
                        )}
                        <p className={actionResult.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {actionResult.message}
                        </p>
                    </div>
                    <button
                        onClick={() => setActionResult(null)}
                        className="mt-2 text-sm text-slate-500 hover:text-slate-700"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-700">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('stats')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'stats'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <ChartBarIcon className="h-5 w-5" />
                            <span>Statistics</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('integrity')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'integrity'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <ShieldCheckIcon className="h-5 w-5" />
                            <span>Integrity</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('cache')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'cache'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <PhotoIcon className="h-5 w-5" />
                            <span>Cache Management</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('documents')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
 activeTab === 'documents'
 ? 'border-primary text-primary'
 : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
 }`}
                    >
                        <div className="flex items-center space-x-2">
                            <DocumentTextIcon className="h-5 w-5" />
                            <span>Documents</span>
                        </div>
                    </button>
                </nav>
            </div>

            {/* Statistics Tab */}
            {activeTab === 'stats' && stats && (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-blue-100 rounded-lg p-2 mr-3">
                                    <DocumentIcon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Documents</p>
                                    <p className="text-xl font-semibold text-slate-900">{stats.totalDocuments?.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-green-100 rounded-lg p-2 mr-3">
                                    <FolderIcon className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Storage Size</p>
                                    <p className="text-xl font-semibold text-slate-900">{formatBytes(stats.totalSizeBytes)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center">
                                <div className="bg-purple-100 rounded-lg p-2 mr-3">
                                    <PhotoIcon className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Evidence Cache Size</p>
                                    <p className="text-xl font-semibold text-slate-900">{formatBytes(stats.evidenceCacheSizeBytes)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Secondary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Documents with PDF</p>
                                    <p className="text-2xl font-semibold text-slate-900">{stats.documentsWithPDF?.toLocaleString()}</p>
                                    <p className="text-sm text-slate-400">
                                        {stats.totalDocuments > 0 ? ((stats.documentsWithPDF / stats.totalDocuments) * 100).toFixed(1) : 0}% of total
                                    </p>
                                </div>
                                <DocumentTextIcon className="h-8 w-8 text-slate-300" />
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Documents with Extracted Text</p>
                                    <p className="text-2xl font-semibold text-slate-900">{stats.documentsWithExtractedText?.toLocaleString()}</p>
                                    <p className="text-sm text-slate-400">
                                        {stats.totalDocuments > 0 ? ((stats.documentsWithExtractedText / stats.totalDocuments) * 100).toFixed(1) : 0}% of total
                                    </p>
                                </div>
                                <DocumentTextIcon className="h-8 w-8 text-slate-300" />
                            </div>
                        </div>
                        <div className="surface-card p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Evidence Cache Entries</p>
                                    <p className="text-2xl font-semibold text-slate-900">{stats.evidenceCacheCount?.toLocaleString()}</p>
                                </div>
                                <PhotoIcon className="h-8 w-8 text-slate-300" />
                            </div>
                        </div>
                    </div>

                    {/* File Type Breakdown */}
                    {stats.documentsByType && stats.documentsByType.length > 0 && (
                        <div className="surface-card p-4">
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">Documents by File Type</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">File Type</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Count</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Total Size</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">% of Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {stats.documentsByType.map((type, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50">
                                                <td className="px-4 py-3">
                                                    <span className="text-slate-900 font-medium">{type.fileType || 'Unknown'}</span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-600">{type.count?.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-slate-600">{formatBytes(type.totalSizeBytes)}</td>
                                                <td className="px-4 py-3 text-slate-500">
                                                    {stats.totalDocuments > 0 ? ((type.count / stats.totalDocuments) * 100).toFixed(1) : 0}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {lastRefresh && (
                        <div className="text-sm text-slate-400 text-center">
                            Last refreshed: {lastRefresh.toLocaleTimeString()}
                        </div>
                    )}
                </div>
            )}

            {/* Integrity Tab */}
            {activeTab === 'integrity' && (
                <div className="space-y-6">
                    {/* Integrity Check Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                            Document Integrity Check
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            Check for missing files, metadata mismatches, and orphaned storage entries.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Tender ID (optional)</label>
                                <input
                                    type="text"
                                    value={integrityTenderId}
                                    onChange={(e) => setIntegrityTenderId(e.target.value)}
                                    placeholder="Check specific tender..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Sample Limit</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="1000"
                                    value={integrityLimit}
                                    onChange={(e) => setIntegrityLimit(parseInt(e.target.value) || 100)}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={handleIntegrityCheck}
                                    disabled={loading}
                                    className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                                >
                                    <ShieldCheckIcon className="h-4 w-4" />
                                    <span>Run Check</span>
                                </button>
                            </div>
                        </div>

                        {/* Integrity Report Results */}
                        {integrityReport && (
                            <div className="mt-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Documents Checked</p>
                                        <p className="text-xl font-semibold text-slate-900">{integrityReport.totalDocuments}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Valid Documents</p>
                                        <p className="text-xl font-semibold text-green-600">{integrityReport.validDocuments}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Issues Found</p>
                                        <p className="text-xl font-semibold text-red-600">
                                            {(integrityReport.missingFiles?.length || 0) + (integrityReport.metadataMismatches?.length || 0)}
                                        </p>
                                    </div>
                                </div>

                                {/* Missing Files */}
                                {integrityReport.missingFiles && integrityReport.missingFiles.length > 0 && (
                                    <div className="border border-red-200 rounded-lg p-4">
                                        <h4 className="text-md font-semibold text-red-600 mb-3 flex items-center">
                                            <XCircleIcon className="h-5 w-5 mr-2" />
                                            Missing Files ({integrityReport.missingFiles.length})
                                        </h4>
                                        <div className="overflow-x-auto max-h-64">
                                            <table className="min-w-full divide-y divide-slate-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Document</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Tender</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Issue</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-200">
                                                    {integrityReport.missingFiles.map((issue, idx) => (
                                                        <tr key={idx}>
                                                            <td className="px-3 py-2 text-sm text-slate-900">{issue.documentTitle}</td>
                                                            <td className="px-3 py-2 text-sm text-slate-500 font-mono">{issue.tenderId?.slice(0, 8)}...</td>
                                                            <td className="px-3 py-2 text-sm text-red-600">{issue.issue}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Orphaned Files */}
                                {integrityReport.orphanedFiles && integrityReport.orphanedFiles.length > 0 && (
                                    <div className="border border-yellow-200 rounded-lg p-4">
                                        <h4 className="text-md font-semibold text-yellow-700 mb-3 flex items-center">
                                            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                            Orphaned Files ({integrityReport.orphanedFiles.length})
                                        </h4>
                                        <div className="overflow-x-auto max-h-64">
                                            <table className="min-w-full divide-y divide-slate-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Storage Key</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Size</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Last Modified</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-200">
                                                    {integrityReport.orphanedFiles.map((file, idx) => (
                                                        <tr key={idx}>
                                                            <td className="px-3 py-2 text-sm text-slate-900 font-mono truncate max-w-xs">{file.storageKey}</td>
                                                            <td className="px-3 py-2 text-sm text-slate-600">{formatBytes(file.sizeBytes)}</td>
                                                            <td className="px-3 py-2 text-sm text-slate-500">{formatDate(file.lastModified)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {integrityReport.missingFiles?.length === 0 && integrityReport.orphanedFiles?.length === 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                                        <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                                        <span className="text-green-700">All checked documents are valid with no issues found.</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Cleanup Orphaned Files Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <TrashIcon className="h-5 w-5 mr-2" />
                            Cleanup Orphaned Files
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            Remove files from storage that are not referenced by any document in the database.
                        </p>
                        <div className="flex items-center space-x-4 mb-4">
                            <label className="flex items-center space-x-2 text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={cleanupDryRun}
                                    onChange={(e) => setCleanupDryRun(e.target.checked)}
                                    className="text-primary focus:ring-primary"
                                />
                                <span>Dry Run (preview only, no deletions)</span>
                            </label>
                            <button
                                onClick={() => setShowCleanupConfirm(true)}
                                disabled={loading}
                                className={`px-4 py-2 rounded-lg ${cleanupDryRun ? 'bg-primary hover:brightness-110' : 'bg-red-600 hover:bg-red-500'} text-on-accent transition-all duration-150 flex items-center space-x-2`}
                            >
                                <TrashIcon className="h-4 w-4" />
                                <span>{cleanupDryRun ? 'Preview Cleanup' : 'Run Cleanup'}</span>
                            </button>
                        </div>

                        {/* Cleanup Results */}
                        {cleanupResult && (
                            <div className={`mt-4 p-4 rounded-lg ${cleanupResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                <div className="flex items-center space-x-3">
                                    {cleanupResult.success ? (
                                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                                    ) : (
                                        <XCircleIcon className="h-6 w-6 text-red-600" />
                                    )}
                                    <div>
                                        <p className={cleanupResult.success ? 'text-green-700' : 'text-red-700'}>
                                            {cleanupResult.dryRun ? 'Dry Run Complete' : 'Cleanup Complete'}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                            Found: {cleanupResult.filesFound} files |
                                            {cleanupResult.dryRun ? ' Would delete' : ' Deleted'}: {cleanupResult.filesDeleted} files |
                                            {cleanupResult.dryRun ? ' Would free' : ' Freed'}: {formatBytes(cleanupResult.bytesFreed)}
                                        </p>
                                        {cleanupResult.errors && cleanupResult.errors.length > 0 && (
                                            <p className="text-sm text-red-600 mt-1">Errors: {cleanupResult.errors.join(', ')}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Verify Tender Storage Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
                            Verify Tender Storage
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            Verify all document files for a specific tender and optionally repair missing entries.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Tender ID</label>
                                <input
                                    type="text"
                                    value={verifyTenderId}
                                    onChange={(e) => setVerifyTenderId(e.target.value)}
                                    placeholder="Enter tender ID..."
                                />
                            </div>
                            <div className="flex items-center">
                                <label className="flex items-center space-x-2 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={verifyRepair}
                                        onChange={(e) => setVerifyRepair(e.target.checked)}
                                        className="text-primary focus:ring-primary"
                                    />
                                    <span>Attempt Repair</span>
                                </label>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => verifyRepair ? setShowVerifyConfirm(true) : handleVerifyStorage()}
                                    disabled={loading || !verifyTenderId.trim()}
                                    className={`px-4 py-2 rounded-lg ${verifyRepair ? 'bg-yellow-500 hover:brightness-110' : 'bg-primary hover:brightness-110'} text-on-accent transition-all duration-150 flex items-center space-x-2 disabled:opacity-50`}
                                >
                                    <ShieldCheckIcon className="h-4 w-4" />
                                    <span>{verifyRepair ? 'Verify & Repair' : 'Verify Only'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Verification Results */}
                        {verificationResult && (
                            <div className="mt-4 space-y-4">
                                <div className={`p-4 rounded-lg ${verificationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                    <div className="flex items-center space-x-3 mb-3">
                                        {verificationResult.success ? (
                                            <CheckCircleIcon className="h-6 w-6 text-green-600" />
                                        ) : (
                                            <XCircleIcon className="h-6 w-6 text-red-600" />
                                        )}
                                        <div>
                                            <p className={verificationResult.success ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                                                Verification {verificationResult.success ? 'Complete' : 'Failed'}
                                            </p>
                                            <p className="text-sm text-slate-500">
                                                Total: {verificationResult.totalDocuments} |
                                                Verified: {verificationResult.verifiedCount} |
                                                Issues: {verificationResult.issuesFound} |
                                                Repaired: {verificationResult.repairedCount}
                                            </p>
                                        </div>
                                    </div>

                                    {verificationResult.details && verificationResult.details.length > 0 && (
                                        <div className="overflow-x-auto max-h-64">
                                            <table className="min-w-full divide-y divide-slate-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Document</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Status</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Original</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">PDF</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Message</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-200">
                                                    {verificationResult.details.map((detail, idx) => (
                                                        <tr key={idx}>
                                                            <td className="px-3 py-2 text-sm text-slate-900">{detail.documentTitle}</td>
                                                            <td className={`px-3 py-2 text-sm font-medium ${getStatusColor(detail.status)}`}>
                                                                {detail.status}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                {detail.originalExists ? (
                                                                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                                                ) : (
                                                                    <XCircleIcon className="h-4 w-4 text-red-600" />
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                {detail.pdfExists ? (
                                                                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                                                ) : (
                                                                    <XCircleIcon className="h-4 w-4 text-red-600" />
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2 text-sm text-slate-500">{detail.message || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cache Management Tab */}
            {activeTab === 'cache' && (
                <div className="space-y-6">
                    {/* Check Cache Status Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                            Check Evidence Cache Status
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            Look up the evidence image cache for a specific document and assessment pair.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Document ID</label>
                                <input
                                    type="text"
                                    value={cacheDocumentId}
                                    onChange={(e) => setCacheDocumentId(e.target.value)}
                                    placeholder="Enter document ID..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Assessment ID</label>
                                <input
                                    type="text"
                                    value={cacheAssessmentId}
                                    onChange={(e) => setCacheAssessmentId(e.target.value)}
                                    placeholder="Enter assessment ID..."
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={handleCheckCacheStatus}
                                    disabled={loading || !cacheDocumentId.trim() || !cacheAssessmentId.trim()}
                                    className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2 disabled:opacity-50"
                                >
                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                    <span>Check Status</span>
                                </button>
                            </div>
                        </div>

                        {/* Cache Status Results */}
                        {cacheStatus && (
                            <div className="mt-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <p className="text-sm text-slate-500">Cached</p>
                                        <p className="text-lg font-semibold">
                                            {cacheStatus.isCached ? (
                                                <span className="text-green-600 flex items-center">
                                                    <CheckCircleIcon className="h-5 w-5 mr-1" /> Yes
                                                </span>
                                            ) : (
                                                <span className="text-red-600 flex items-center">
                                                    <XCircleIcon className="h-5 w-5 mr-1" /> No
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Snippets</p>
                                        <p className="text-lg font-semibold text-slate-900">{cacheStatus.snippetCount}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Full Pages</p>
                                        <p className="text-lg font-semibold text-slate-900">{cacheStatus.fullPageCount}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Cache Size</p>
                                        <p className="text-lg font-semibold text-slate-900">{formatBytes(cacheStatus.cacheSizeBytes)}</p>
                                    </div>
                                </div>
                                {cacheStatus.generatedAt && (
                                    <p className="text-sm text-slate-500 mb-4">
                                        <ClockIcon className="h-4 w-4 inline mr-1" />
                                        Generated: {formatDate(cacheStatus.generatedAt)}
                                    </p>
                                )}
                                {cacheStatus.cacheKeyPrefix && (
                                    <p className="text-sm text-slate-400 font-mono mb-4 truncate">
                                        Key: {cacheStatus.cacheKeyPrefix}
                                    </p>
                                )}

                                {/* Cache Actions */}
                                {cacheStatus.isCached && (
                                    <div className="flex space-x-3 pt-4 border-t border-slate-200">
                                        <button
                                            onClick={() => setShowClearCacheConfirm(true)}
                                            className="px-4 py-2 rounded-lg bg-red-600 text-on-accent hover:bg-red-500 transition-all duration-150 flex items-center space-x-2"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                            <span>Clear Cache</span>
                                        </button>
                                        <button
                                            onClick={() => setShowRegenerateConfirm(true)}
                                            className="px-4 py-2 rounded-lg bg-yellow-500 text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2"
                                        >
                                            <ArrowPathIcon className="h-4 w-4" />
                                            <span>Regenerate Images</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Clear All Assessment Caches Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <TrashIcon className="h-5 w-5 mr-2" />
                            Clear All Caches for Assessment
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            Clear all evidence image caches for a specific assessment (affects all documents).
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-500 mb-2">Assessment ID</label>
                                <input
                                    type="text"
                                    value={clearAssessmentId}
                                    onChange={(e) => setClearAssessmentId(e.target.value)}
                                    placeholder="Enter assessment ID..."
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => setShowClearAssessmentCacheConfirm(true)}
                                    disabled={loading || !clearAssessmentId.trim()}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-on-accent hover:bg-red-500 transition-all duration-150 flex items-center space-x-2 disabled:opacity-50"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    <span>Clear All Caches</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
                <div className="space-y-6">
                    {/* List Tender Documents Section */}
                    <div className="surface-card p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                            <FolderIcon className="h-5 w-5 mr-2" />
                            List Tender Documents
                        </h3>
                        <p className="text-slate-500 text-sm mb-4">
                            View all documents for a tender with detailed storage information including S3 file existence.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-500 mb-2">Tender ID</label>
                                <input
                                    type="text"
                                    value={docTenderId}
                                    onChange={(e) => setDocTenderId(e.target.value)}
                                    placeholder="Enter tender ID..."
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => handleListTenderDocuments()}
                                    disabled={loading || !docTenderId.trim()}
                                    className="px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150 flex items-center space-x-2 disabled:opacity-50"
                                >
                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                    <span>List Documents</span>
                                </button>
                            </div>
                        </div>

                        {/* Document List Results */}
                        {tenderDocuments && (
                            <div className="mt-6 space-y-4">
                                {/* Summary */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Total Documents</p>
                                        <p className="text-xl font-semibold text-slate-900">{tenderDocuments.totalCount}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Total Storage</p>
                                        <p className="text-xl font-semibold text-slate-900">{formatBytes(tenderDocuments.totalStorageBytes)}</p>
                                    </div>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <p className="text-sm text-slate-500">Tender ID</p>
                                        <p className="text-sm font-mono text-slate-600 truncate">{tenderDocuments.tenderId}</p>
                                    </div>
                                </div>

                                {/* Delete All Documents Section */}
                                {tenderDocuments.totalCount > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <h4 className="text-md font-semibold text-red-600 mb-3 flex items-center">
                                            <TrashIcon className="h-5 w-5 mr-2" />
                                            Delete All Documents
                                        </h4>
                                        <p className="text-sm text-slate-500 mb-3">
                                            Delete all {tenderDocuments.totalCount} documents for this tender including DB records, S3 files, and snippet caches.
                                        </p>
                                        <div className="flex items-center space-x-4">
                                            <label className="flex items-center space-x-2 text-sm text-slate-600">
                                                <input
                                                    type="checkbox"
                                                    checked={deleteAllDryRun}
                                                    onChange={(e) => setDeleteAllDryRun(e.target.checked)}
                                                    className="text-primary focus:ring-primary"
                                                />
                                                <span>Dry Run (preview only)</span>
                                            </label>
                                            <button
                                                onClick={() => setShowDeleteAllDocumentsConfirm(true)}
                                                disabled={loading}
                                                className={`px-4 py-2 rounded-lg ${deleteAllDryRun ? 'bg-yellow-500 hover:brightness-110' : 'bg-red-600 hover:bg-red-500'} text-on-accent transition-all duration-150 flex items-center space-x-2`}
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                                <span>{deleteAllDryRun ? 'Preview Deletion' : 'Delete All'}</span>
                                            </button>
                                        </div>

                                        {/* Delete All Result */}
                                        {deleteAllResult && (
                                            <div className={`mt-4 p-3 rounded-lg ${deleteAllResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                                <p className={deleteAllResult.success ? 'text-green-700' : 'text-red-700'}>
                                                    {deleteAllResult.dryRun ? 'Dry Run Complete' : 'Deletion Complete'}
                                                </p>
                                                <p className="text-sm text-slate-500 mt-1">
                                                    {deleteAllResult.dryRun ? 'Would delete' : 'Deleted'}: {deleteAllResult.documentsDeleted} documents, {deleteAllResult.filesDeleted} files, {deleteAllResult.snippetsCleaned} snippets |
                                                    {deleteAllResult.dryRun ? ' Would free' : ' Freed'}: {formatBytes(deleteAllResult.bytesFreed)}
                                                </p>
                                                {deleteAllResult.errors && deleteAllResult.errors.length > 0 && (
                                                    <p className="text-sm text-red-600 mt-1">Errors: {deleteAllResult.errors.join(', ')}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Documents Table */}
                                {tenderDocuments.documents && tenderDocuments.documents.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-slate-200">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Size</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">State</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Original</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">PDF</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Created</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {tenderDocuments.documents.map((doc) => (
                                                    <tr key={doc.id} className="hover:bg-slate-50">
                                                        <td className="px-4 py-3">
                                                            <div className="max-w-xs truncate">
                                                                <span className="text-slate-900 font-medium">{doc.title}</span>
                                                                <p className="text-xs text-slate-400 font-mono truncate">{doc.id}</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-600">{doc.fileType || '-'}</td>
                                                        <td className="px-4 py-3 text-slate-600">{formatBytes(doc.fileSize)}</td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-1 rounded text-xs ${getDocumentStateColor(doc.documentState)}`}>
                                                                {doc.documentState}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {doc.originalExists ? (
                                                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                                            ) : (
                                                                <XCircleIcon className="h-5 w-5 text-red-600" />
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {doc.isConverted ? (
                                                                doc.pdfExists ? (
                                                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                                                ) : (
                                                                    <XCircleIcon className="h-5 w-5 text-red-600" />
                                                                )
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-500 text-sm">
                                                            {formatDate(doc.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedDocument(doc);
                                                                    setShowDeleteDocumentConfirm(true);
                                                                }}
                                                                className="p-1 rounded text-red-600 hover:text-red-500 hover:bg-red-50"
                                                                title="Delete document"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                                        <DocumentIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                                        <p className="text-slate-500">No documents found for this tender.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Modals */}
            <ModalConfirm
                isOpen={showCleanupConfirm}
                onClose={() => setShowCleanupConfirm(false)}
                onConfirm={handleCleanupOrphaned}
                title={cleanupDryRun ? "Preview Cleanup" : "Confirm Cleanup"}
                message={cleanupDryRun
                    ? "This will scan for orphaned files and show what would be deleted without actually removing anything."
                    : "This will permanently delete orphaned files from storage. This action cannot be undone."
                }
                confirmLabel={cleanupDryRun ? "Preview" : "Delete Files"}
                type={cleanupDryRun ? "info" : "danger"}
            />

            <ModalConfirm
                isOpen={showClearCacheConfirm}
                onClose={() => setShowClearCacheConfirm(false)}
                onConfirm={handleClearCache}
                title="Clear Evidence Cache"
                message="This will delete all cached evidence images for this document/assessment pair. They will be regenerated on next access."
                confirmLabel="Clear Cache"
                type="danger"
            />

            <ModalConfirm
                isOpen={showClearAssessmentCacheConfirm}
                onClose={() => setShowClearAssessmentCacheConfirm(false)}
                onConfirm={handleClearAssessmentCache}
                title="Clear All Assessment Caches"
                message="This will delete ALL cached evidence images for this assessment across all documents. This may affect multiple cache entries."
                confirmLabel="Clear All"
                type="danger"
            />

            <ModalConfirm
                isOpen={showRegenerateConfirm}
                onClose={() => setShowRegenerateConfirm(false)}
                onConfirm={handleRegenerateImages}
                title="Regenerate Evidence Images"
                message="This will clear existing cache and regenerate all evidence images for this document/assessment pair. This may take some time."
                confirmLabel="Regenerate"
                type="warning"
            />

            <ModalConfirm
                isOpen={showVerifyConfirm}
                onClose={() => setShowVerifyConfirm(false)}
                onConfirm={handleVerifyStorage}
                title="Verify & Repair Storage"
                message="This will verify document storage and attempt to repair any issues found. Some repairs may modify database records."
                confirmLabel="Verify & Repair"
                type="warning"
            />

            <ModalConfirm
                isOpen={showDeleteDocumentConfirm}
                onClose={() => {
                    setShowDeleteDocumentConfirm(false);
                    setSelectedDocument(null);
                }}
                onConfirm={handleDeleteDocument}
                title="Delete Document"
                message={`Are you sure you want to delete "${selectedDocument?.title}"? This will remove the database record, S3 files, and any snippet caches. This action cannot be undone.`}
                confirmLabel="Delete Document"
                type="danger"
            />

            <ModalConfirm
                isOpen={showDeleteAllDocumentsConfirm}
                onClose={() => setShowDeleteAllDocumentsConfirm(false)}
                onConfirm={handleDeleteAllTenderDocuments}
                title={deleteAllDryRun ? "Preview Document Deletion" : "Delete All Tender Documents"}
                message={deleteAllDryRun
                    ? `This will preview what would be deleted for tender ${docTenderId}. No actual deletions will occur.`
                    : `Are you sure you want to delete ALL documents for tender ${docTenderId}? This will remove all database records, S3 files, and snippet caches. This action cannot be undone.`
                }
                confirmLabel={deleteAllDryRun ? "Preview" : "Delete All Documents"}
                type={deleteAllDryRun ? "warning" : "danger"}
            />
        </div>
    );
};

export default Storage;
